import { db } from '../lib/firebase';
import { sendNotification } from './notificationService';
import {
  ref,
  set,
  get,
  update,
  remove,
  onValue,
  off,
  serverTimestamp,
  runTransaction
} from 'firebase/database';
import { WordValidationResult, calculateStopScores } from './stopGameValidator';

export const AVAILABLE_STOP_LETTERS = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J',
  'K', 'L', 'M', 'N', 'O', 'P', 'R', 'S', 'T', 'U', 'V', 'W'
];

export interface RoomPlayer {
  id: string;
  name: string;
  avatarUrl?: string;
  isHost: boolean;
  totalScore: number;
  roundScore: number;
  categoryScores?: Record<string, number>;
  answers?: Record<string, string>;
  isReady?: boolean;
}

export interface StopGameRoom {
  code: string;
  status: 'lobby' | 'playing' | 'validating' | 'round_summary' | 'game_over';
  hostId: string;
  currentRound: number;
  totalRounds: number;
  roundDuration: number; // in seconds
  currentLetter: string;
  usedLetters: string[];
  categoryIds: string[];
  players: Record<string, RoomPlayer>;
  stoppedByPlayerId?: string | null;
  stopTimestamp?: number | null;
  roundStartTime?: number;
  roundEndTime?: number;
  validations?: Record<string, Record<string, WordValidationResult>> | null;
  votes?: Record<string, Record<string, Record<string, boolean>>> | null;
  createdAt: number | object;
  lastActivity: number | object;
}

export interface CreateRoomOptions {
  totalRounds?: number;
  roundDuration?: number;
  categoryIds?: string[];
}

/**
 * Generates a unique, readable 5-character room code (e.g., "STOP9", "A4B7K")
 */
export function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 5; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Returns a random letter that has not yet been used in previous rounds.
 */
export function getRandomUnusedLetter(usedLetters: string[] = []): string {
  const available = AVAILABLE_STOP_LETTERS.filter(letter => !usedLetters.includes(letter));
  if (available.length === 0) {
    // If all letters used, reset pool
    return AVAILABLE_STOP_LETTERS[Math.floor(Math.random() * AVAILABLE_STOP_LETTERS.length)];
  }
  return available[Math.floor(Math.random() * available.length)];
}

/**
 * Creates a new STOP game room in Firebase Realtime Database.
 */
export async function createRoom(
  host: { id: string; name: string; avatarUrl?: string },
  options: CreateRoomOptions = {}
): Promise<string> {
  const roomCode = generateRoomCode();
  const roomRef = ref(db, `stopGameRooms/${roomCode}`);

  const defaultCategories = ['animal', 'food', 'country_city', 'job', 'object', 'verb'];
  const newRoom: StopGameRoom = {
    code: roomCode,
    status: 'lobby',
    hostId: host.id,
    currentRound: 0,
    totalRounds: options.totalRounds || 3,
    roundDuration: options.roundDuration || 60,
    currentLetter: '',
    usedLetters: [],
    categoryIds: options.categoryIds || defaultCategories,
    players: {
      [host.id]: {
        id: host.id,
        name: host.name,
        avatarUrl: host.avatarUrl || '',
        isHost: true,
        totalScore: 0,
        roundScore: 0,
        isReady: true
      }
    },
    createdAt: serverTimestamp(),
    lastActivity: serverTimestamp()
  };

  await set(roomRef, newRoom);
  return roomCode;
}

/**
 * Joins an existing game room.
 */
export async function joinRoom(
  roomCode: string,
  player: { id: string; name: string; avatarUrl?: string }
): Promise<StopGameRoom> {
  const cleanCode = roomCode.trim().toUpperCase();
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) {
    throw new Error('Room not found. Check the room code.');
  }

  const room = snapshot.val() as StopGameRoom;

  const existingPlayer = room.players?.[player.id];

  // Add or update player in players map while preserving existing round progress
  const updatedPlayer: RoomPlayer = {
    id: player.id,
    name: player.name,
    avatarUrl: player.avatarUrl || '',
    isHost: player.id === room.hostId,
    totalScore: existingPlayer?.totalScore || 0,
    roundScore: existingPlayer?.roundScore || 0,
    isReady: true
  };

  if (existingPlayer?.answers) {
    updatedPlayer.answers = existingPlayer.answers;
  }

  await update(ref(db, `stopGameRooms/${cleanCode}/players/${player.id}`), updatedPlayer);
  await update(ref(db, `stopGameRooms/${cleanCode}`), { lastActivity: serverTimestamp() });

  return room;
}

/**
 * Leave a room.
 */
export async function leaveRoom(roomCode: string, playerId: string): Promise<void> {
  const cleanCode = roomCode.trim().toUpperCase();
  const playerRef = ref(db, `stopGameRooms/${cleanCode}/players/${playerId}`);
  await remove(playerRef);

  // Check if room is now empty
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);
  const snapshot = await get(roomRef);
  if (snapshot.exists()) {
    const room = snapshot.val() as StopGameRoom;
    const remainingPlayerIds = Object.keys(room.players || {});
    if (remainingPlayerIds.length === 0) {
      await remove(roomRef);
    } else if (room.hostId === playerId) {
      // Transfer host to first remaining player
      const newHostId = remainingPlayerIds[0];
      await update(roomRef, {
        hostId: newHostId,
        [`players/${newHostId}/isHost`]: true
      });
    }
  }
}

/**
 * Fetches a room snapshot directly from Firebase RTDB.
 */
export async function getRoom(roomCode: string): Promise<StopGameRoom | null> {
  const cleanCode = roomCode.trim().toUpperCase();
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) return null;
  return snapshot.val() as StopGameRoom;
}

/**
 * Updates room settings such as roundDuration, totalRounds, categoryIds.
 */
export async function updateRoomSettings(
  roomCode: string,
  settings: Partial<CreateRoomOptions>
): Promise<void> {
  const cleanCode = roomCode.trim().toUpperCase();
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);
  await update(roomRef, settings);
}

/**
 * Start a round (Host only).
 * If transitioning from round_summary at the final round, transitions to game_over.
 * If in game_over, restarts a rematch from round 1.
 */
export async function startRound(roomCode: string): Promise<void> {
  const cleanCode = roomCode.trim().toUpperCase();
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) throw new Error('Room not found');
  const room = snapshot.val() as StopGameRoom;

  const isFinalRoundSummary = room.status === 'round_summary' && (room.currentRound || 1) >= (room.totalRounds || 3);

  // If transitioning from round_summary, finalize total scores with any player votes applied
  const updatedPlayers = { ...room.players };
  if (room.status === 'round_summary' && room.validations) {
    const playerAnswers: Record<string, Record<string, string>> = {};
    for (const pId of Object.keys(updatedPlayers)) {
      playerAnswers[pId] = updatedPlayers[pId].answers || {};
    }
    const finalVotedScores = calculateStopScores(playerAnswers, room.validations, room.votes);
    for (const pId of Object.keys(updatedPlayers)) {
      const priorRoundScore = updatedPlayers[pId].roundScore || 0;
      const actualVotedRoundScore = finalVotedScores[pId]?.roundScore ?? priorRoundScore;
      const scoreDiff = actualVotedRoundScore - priorRoundScore;
      updatedPlayers[pId].totalScore = Math.max(0, (updatedPlayers[pId].totalScore || 0) + scoreDiff);
    }
  }

  // If host clicked "See Final Podium" after the final round summary
  if (isFinalRoundSummary) {
    await update(roomRef, {
      status: 'game_over',
      players: updatedPlayers,
      lastActivity: serverTimestamp()
    });
    return;
  }

  const isRematch = room.status === 'game_over';
  const nextRoundNumber = isRematch ? 1 : (room.currentRound || 0) + 1;
  const usedLettersPool = isRematch ? [] : (room.usedLetters || []);
  const newLetter = getRandomUnusedLetter(usedLettersPool);
  const updatedUsedLetters = isRematch ? [newLetter] : [...(room.usedLetters || []), newLetter];

  // Reset player answers and round scores for new round (and reset totalScore on rematch)
  for (const pId of Object.keys(updatedPlayers)) {
    updatedPlayers[pId] = {
      ...updatedPlayers[pId],
      totalScore: isRematch ? 0 : (updatedPlayers[pId].totalScore || 0),
      roundScore: 0,
      categoryScores: {},
      answers: {}
    };
  }

  const roundDuration = room.roundDuration || 60;
  const roundStartTime = Date.now() + 3000;
  const roundEndTime = roundStartTime + roundDuration * 1000;

  await update(roomRef, {
    status: 'playing',
    currentRound: nextRoundNumber,
    currentLetter: newLetter,
    usedLetters: updatedUsedLetters,
    stoppedByPlayerId: null,
    stopTimestamp: null,
    roundStartTime,
    roundEndTime,
    players: updatedPlayers,
    validations: null,
    votes: null,
    lastActivity: serverTimestamp()
  });
}

/**
 * Skips the current round's letter and generates a new unused letter.
 */
export async function skipRoundLetter(roomCode: string): Promise<void> {
  const cleanCode = roomCode.trim().toUpperCase();
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) return;
  const room = snapshot.val() as StopGameRoom;

  const newLetter = getRandomUnusedLetter(room.usedLetters || []);
  const updatedUsedLetters = [...(room.usedLetters || []), newLetter];

  const updatedPlayers = { ...room.players };
  for (const pId of Object.keys(updatedPlayers)) {
    updatedPlayers[pId] = {
      ...updatedPlayers[pId],
      answers: {}
    };
  }

  const roundDuration = room.roundDuration || 60;
  const roundStartTime = Date.now() + 3000;
  const roundEndTime = roundStartTime + roundDuration * 1000;

  await update(roomRef, {
    currentLetter: newLetter,
    usedLetters: updatedUsedLetters,
    roundStartTime,
    roundEndTime,
    players: updatedPlayers,
    lastActivity: serverTimestamp()
  });
}

export interface PendingVoter {
  playerId: string;
  name: string;
  pendingCount: number;
}

/**
 * Checks which players have not finished voting on other players' non-empty words.
 * Returns an array of players who still have pending votes.
 */
export function checkPendingVoters(room: StopGameRoom): PendingVoter[] {
  if (!room || !room.players) return [];

  const players = room.players;
  const playerIds = Object.keys(players);
  const categoryIds = room.categoryIds || [];
  const votes = room.votes || {};

  const pendingList: PendingVoter[] = [];

  for (const voterId of playerIds) {
    let pendingCount = 0;

    for (const targetPlayerId of playerIds) {
      if (targetPlayerId === voterId) continue; // Players do not vote on their own words

      const targetAnswers = players[targetPlayerId]?.answers || {};

      for (const catId of categoryIds) {
        const rawWord = targetAnswers[catId] || '';
        const cleanWord = rawWord.trim();

        // Only non-empty words need to be voted on
        if (cleanWord.length > 0) {
          const hasVoted = votes[targetPlayerId]?.[catId]?.[voterId] !== undefined;
          if (!hasVoted) {
            pendingCount++;
          }
        }
      }
    }

    if (pendingCount > 0) {
      pendingList.push({
        playerId: voterId,
        name: players[voterId]?.name || 'Player',
        pendingCount
      });
    }
  }

  return pendingList;
}

/**
 * Casts a vote on another player's word in a specific category.
 * Voter cannot vote on their own word.
 * If isValidVote is null, removes the vote.
 */
export async function voteOnWord(
  roomCode: string,
  voterPlayerId: string,
  targetPlayerId: string,
  categoryId: string,
  isValidVote: boolean | null
): Promise<void> {
  if (voterPlayerId === targetPlayerId) return;
  const cleanCode = roomCode.trim().toUpperCase();
  const voteRef = ref(db, `stopGameRooms/${cleanCode}/votes/${targetPlayerId}/${categoryId}/${voterPlayerId}`);
  if (isValidVote === null) {
    await remove(voteRef);
  } else {
    await set(voteRef, isValidVote);
  }
}

/**
 * Triggers STOP for all players.
 */
export async function callStop(roomCode: string, playerId: string): Promise<void> {
  const cleanCode = roomCode.trim().toUpperCase();
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);

  await update(roomRef, {
    status: 'validating',
    stoppedByPlayerId: playerId,
    stopTimestamp: Date.now(),
    lastActivity: serverTimestamp()
  });
}

/**
 * Player submits their answers for the round.
 */
export async function submitAnswers(
  roomCode: string,
  playerId: string,
  answers: Record<string, string>
): Promise<void> {
  const cleanCode = roomCode.trim().toUpperCase();
  const playerAnswersRef = ref(db, `stopGameRooms/${cleanCode}/players/${playerId}/answers`);
  await set(playerAnswersRef, answers);
}

/**
 * Stores validation results and updates round & total scores.
 * Transitions to round_summary so all players can review words and vote.
 */
export async function finalizeRoundScores(
  roomCode: string,
  validations: Record<string, Record<string, WordValidationResult>>
): Promise<void> {
  const cleanCode = roomCode.trim().toUpperCase();
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) return;
  const room = snapshot.val() as StopGameRoom;

  // Extract all player answers
  const playerAnswers: Record<string, Record<string, string>> = {};
  const players = room.players || {};

  for (const pId of Object.keys(players)) {
    playerAnswers[pId] = players[pId].answers || {};
  }

  const scores = calculateStopScores(playerAnswers, validations);

  // Update players
  const updatedPlayers = { ...players };
  for (const pId of Object.keys(updatedPlayers)) {
    const pScore = scores[pId] || { roundScore: 0, categoryScores: {} };
    updatedPlayers[pId].roundScore = pScore.roundScore;
    updatedPlayers[pId].categoryScores = pScore.categoryScores;
    updatedPlayers[pId].totalScore = (updatedPlayers[pId].totalScore || 0) + pScore.roundScore;
  }

  // Always transition to round_summary for review & voting
  await update(roomRef, {
    status: 'round_summary',
    players: updatedPlayers,
    validations,
    lastActivity: serverTimestamp()
  });
}

/**
 * Subscribes to real-time room updates.
 */
export function listenToRoom(
  roomCode: string,
  onUpdate: (room: StopGameRoom | null) => void
): () => void {
  const cleanCode = roomCode.trim().toUpperCase();
  const roomRef = ref(db, `stopGameRooms/${cleanCode}`);

  const unsubscribe = onValue(
    roomRef,
    snapshot => {
      if (snapshot.exists()) {
        onUpdate(snapshot.val() as StopGameRoom);
      } else {
        onUpdate(null);
      }
    },
    error => {
      console.error('Error listening to STOP room:', error);
      onUpdate(null);
    }
  );

  return () => {
    off(roomRef);
  };
}

/**
 * Invites a friend to an existing STOP game room and sends a push notification.
 */
export async function inviteFriendToStopGame(
  roomCode: string,
  host: { id: string; name: string; avatarUrl?: string },
  friendId: string,
  _friendName?: string
): Promise<void> {
  const cleanCode = roomCode.trim().toUpperCase();
  await sendNotification(friendId, {
    type: 'stop_game_invite',
    message: `${host.name} invited you to play STOP! 🛑`,
    fromUserId: host.id,
    fromUserName: host.name,
    fromUserPhoto: host.avatarUrl,
    metadata: {
      roomCode: cleanCode,
      hostId: host.id,
      hostName: host.name
    }
  });
}

/**
 * Creates a new STOP game room with a friend and sends them a push notification to join.
 */
export async function createStopGameWithFriend(
  host: { id: string; name: string; avatarUrl?: string },
  friend: { id: string; name: string; avatarUrl?: string },
  options: CreateRoomOptions = {}
): Promise<string> {
  const roomCode = await createRoom(host, options);
  await inviteFriendToStopGame(roomCode, host, friend.id, friend.name);
  return roomCode;
}
